import React from 'react';
import {
  Radio, Button, Icon, Divider, Select,
} from 'antd';
import { isEmpty, find, flatten } from 'lodash';
import { css } from 'emotion';
import moment from 'moment';
import {
  Wrapper, AreaTable, DataTable, ExpandButton, Loading, TableWrapper, ToolbarSection, DayViewSelect, Option
} from './styles';
import PageWithTopbar from '../PageWithTopbar';
import VolumePicker from './VolumePicker';
import VolumeEditor from './VolumeEditor';
import { VOLUME_MODIFIERS, WEEK_DAYS, FRIENDLY_WEEK_DAY_ARRAY } from '../../constants/dataPages.constants';
import { DATE_FORMAT } from '../../constants/grid.constants';
import DisabledTooltip from '../DisabledTooltip';


const WEEK = 'week';
const DAY = 'day';

const WEEK_COLUMN_KEY = 'day';
const DAY_COLUMN_KEY = 'shift_block_time';

const getColumnSource = timePeriod => timePeriod === WEEK ? WEEK_COLUMN_KEY : DAY_COLUMN_KEY;
class VolumeManager extends React.Component {
  constructor(props) {
    super(props);
    const { availableVolumeDataSources, orderDateDefaultVolumeDate } = this.props;
    this.state = {
      volumeType: 'volume',
      dateType: orderDateDefaultVolumeDate ? 'order_datetime' : 'datetime',
      source: availableVolumeDataSources ? availableVolumeDataSources[0] : null,
      weeks: [],
      // TODO: Revert to all days when real data can be used.
      days: Object.keys(WEEK_DAYS),
      selectedAreas: {},
      editRows: {},
      expandedRows: [],
      // TODO: Revert to week when real data can be used.
      timePeriod: 'week',
    };
  }

  componentDidUpdate(prevProps) {
    const { availableVolumeDataSources, persistParams } = this.props;
    if (!prevProps) return false;
    if (availableVolumeDataSources.join('|') === prevProps.availableVolumeDataSources.join('|')) {
      if (JSON.stringify(persistParams) !== JSON.stringify(prevProps.persistParams)) {
        this.onApply(this.state); // If week changes, the sourced data needs to update too.
      }
      return false;
    }
    const { source } = this.state;
    const found = availableVolumeDataSources.find(item => item === source);
    if (!found) this.clearSourceSelction();
    return true;
  }

  clearSourceSelction = () => {
    this.setState({ source: null }, () => this.onApply(this.state));
  }

  mapVolumes = (volumes, source) => {
    const { timePeriod } = this.state;
    const columnKey = getColumnSource(timePeriod);
    const mappedVolumes = !isEmpty(volumes)
      ? volumes
        .map(({ area, data }) => ({
          key: area,
          area,
          data: [data.reduce((a, c) => ({
            ...a, key: `${area}${source}`, area, source, [c[columnKey]]: c.amount,
          }), {})],
        }))
      : [];
    return mappedVolumes;
  }
  volumeComponent = (props) => {
    const { selectedAreas } = this.state;
    const { area: { area, source } } = props;
    return <VolumeEditor {...props} disabled={selectedAreas[area] !== source} />;
  }

  dataTableRender = ({ data }) => {
    const { selectedAreas } = this.state;
    return (
      <DataTable
        rowSelection={{
          type: 'radio',
          getCheckboxProps: record => ({
            style: { display: !record.key ? 'none' : 'inherit' },
          }),

          onSelect: this.areaSelect,
          selectedRowKeys: Object.keys(selectedAreas)
            .map(area => `${area}${selectedAreas[area]}`),
        }}
        showHeader={false}
        size="middle"
        columns={this.getColumns()}
        dataSource={data}
        pagination={false}
      />
    );
  }

  areaSelect = (row) => {
    const { selectedAreas, editRows } = this.state;
    const grabRowData = { ...row };
    // Backend really need to return a better object so that I don't have to
    // hardcode this part.
    delete grabRowData.area;
    delete grabRowData.key;
    delete grabRowData.source;

    // Upon selection, repopulate the edit row so that the data in
    // selected row can be saved.
    const { source } = row;
    const prePopulateData = {};
    Object.keys(grabRowData).forEach((key) => {
      prePopulateData[key] = {
        source, volume: grabRowData[key], modifier: 'none', amount: 0,
      };
    });

    return this.setState({
      editRows: {
        ...editRows,
        [row.area]: { ...prePopulateData },
      },
      selectedAreas: {
        ...selectedAreas,
        [row.area]: row.source,
      },
    });
  }

  volumeSelect = newState => this.setState({
    ...newState,
    selectedAreas: {},
    editRows: {},
  });

  // Take a list of modifications and add them to the cumulative modification object.
  // Fills an area with default edits if there's at least one edit in the area's row.
  modifyVolume = ({
    area: { area, source }, day, volume, modifier, amount,
  }) => {
    const { editRows, timePeriod } = this.state;
    const { volumeFromSource } = this.props;

    let newEditRows;

    // If the row has been edited before and store in state, please don't calculate the default
    // values, or vice versa.
    if (editRows === {}) {
      // Generating the default row object.
      newEditRows = (isEmpty(volumeFromSource) || source === 'current')
        ? {}
        : {
          [area]: find(volumeFromSource, { area }).data
            .reduce((a, c) => ({
              ...a,
              [c.day]: {
                source, modifier: 'none', volume: c.amount, amount: 0,
              },
            }), {}),
        };
    } else {
      newEditRows = editRows;
    }

    const newRowsObj = {
      ...newEditRows,
      [area]: {
        ...newEditRows[area],
        ...{
          [day]: {
            source, modifier, volume, amount,
          },
        },
      },
    };

    return this.setState({
      editRows: newRowsObj,
    });
  }

  // Add a calculated row to display the modifications made to each sub table.
  appendModificationRow = (tableData) => {
    const { editRows } = this.state;
    return tableData
      .map(({ area, data }) => ({
        area,
        data: [...data, { area, source: 'modification', ...editRows[area] }],
      }));
  }

  // Adds lists of rows together with respect to nestedness.
  addRows = (data, newSources) => newSources
    .map(({
      key, area, source, ...days
    }) => ({
      ...find(data, { source }),
      area: 'total',
      source,
      // Native ES object mapping (Builds up an object from a list of keys).
      ...Object.keys(days)
        .reduce((a, day) => {
          // Finds the correct things to add up inside the list of rows.
          const base = (find(data, { source }) || {})[day] || 0;
          const toAdd = find(newSources, { source })[day];
          return {
            ...a,
            [day]: base + (toAdd.modifier
              ? VOLUME_MODIFIERS[toAdd.modifier].f(toAdd.volume, toAdd.amount)
              : toAdd
            ),
          };
        }, {}),
    }))

  // Sums a list of rows while maintaining their structure.
  appendTotalRow = (tableData) => {
    const total = tableData
      .reduce((a, c) => ({
        ...a,
        data: this.addRows(a.data, c.data),
      }), {
        area: 'total',
        data: [],
      });

    // Total modification row should include the current value if nothing is selected
    // to reflect the actual number.
    tableData.forEach((tData) => {
      // If modification has data, don't use current...next.
      if (Object.keys(tData.data[tData.data.length - 1]).length > 2) return false;
      const {
        area, key, source, ...days
      } = tData.data[0]; // Use current.

      // adding values from object to total modification object.
      const modRow = find(total.data, { source: 'modification' });
      return Object.keys(days).forEach((dayKey) => {
        if (!modRow[dayKey]) modRow[dayKey] = 0;
        modRow[dayKey] += parseInt(tData.data[0][dayKey], 0);
      });
    });

    return [...tableData, total];
  }

  // Folds two volume data objects together. Usually used with Current and [Some Other Source].
  combineVolumes = (currentVolume, volumeFromSource, source) => {
    const current = this.mapVolumes(currentVolume, 'current');
    const volume = this.mapVolumes(volumeFromSource, source);
    const toMerge = area => find(volume, { area });
    const currentRowMap = current
      .map(({ area, data }) => ({
        area,
        data: [
          ...data,
          // eslint-disable-next-line no-nested-ternary
          ...toMerge(area)
            ? toMerge(area).data
            : (source ? [{ area, source }] : []),
        ],
      }));

    return this.appendTotalRow(
      this.appendModificationRow(currentRowMap),
    );
  }

  onExpand = (key) => {
    const { expandedRows } = this.state;
    return expandedRows.includes(key)
      ? this.setState({ expandedRows: expandedRows.filter(row => row !== key) })
      : this.setState({ expandedRows: [...expandedRows, key] });
  }

  toggleExpandAll = (rows) => {
    const { expandedRows } = this.state;
    return expandedRows.length === rows.length
      ? this.setState({ expandedRows: [] })
      : this.setState({ expandedRows: rows.map(({ area }) => area) });
  }

  getEdits = () => {
    const {
      volumeType, dateType, weeks, editRows, timePeriod,
    } = this.state;

    const {
      persistParams: { startOfWeekDate, endOfWeekDate },
      currentVolume: { data },
    } = this.props;

    const columnKey = getColumnSource(timePeriod);

    // Why can't backend just return me dictionaries?
    // I have to write this to process list within a list.
    const getCurrentValue = (area, column) => data && (
      ((data.find(row => row.area === area)).data
        .find(item => item[columnKey] === column)).amount
    );

    const dayProperty = columnKey === DAY_COLUMN_KEY ? { day: timePeriod } : {};

    // Turn the Modifier state object into POST-friendly JSON.
    const modifiers = flatten(Object.keys(editRows)
      .map(area => Object.keys(editRows[area])
        .map((column) => {
          const {
            modifier, volume, amount, source,
          } = editRows[area][column];
          return {
            data_source: source,
            area,
            [columnKey]: column,
            operation: modifier,
            amount,
            current_value: getCurrentValue(area, column),
            original_total: volume,
            modified_total: VOLUME_MODIFIERS[modifier].f(volume, amount),
            ...dayProperty,
          };
        })));

    const weekOr = (week, alt) => week ? week.format(DATE_FORMAT) : alt;

    return {
      data_type: volumeType,
      date_type_field: dateType,
      source_from_date: weekOr(weeks[0], startOfWeekDate),
      source_to_date: weekOr(weeks[1], endOfWeekDate),
      modifiers,
    };
  }

  datesOverlap = ([start, end]) => {
    const { persistParams: { startOfWeekDate, endOfWeekDate } } = this.props;
    return start <= moment(endOfWeekDate) && end >= moment(startOfWeekDate);
  }

  applyVolumeWithTimePeriod(params, callback) {
    const { onApplyVolume } = this.props;
    const { timePeriod } = this.state;
    const requestTimePeriod = timePeriod === WEEK ? WEEK : DAY;
    onApplyVolume({
      ...this.state,
      ...params,
      timePeriod: requestTimePeriod,
    }, callback);
  }

  onApply = (toApply) => {
    this.applyVolumeWithTimePeriod({ ...this.state, ...toApply }, () => this.volumeSelect(toApply));
  };

  onSetTimePeriod(timePeriod) {
    const isWeekTimePeriod = timePeriod === 'week';
    const days = isWeekTimePeriod ? Object.keys(WEEK_DAYS) : [timePeriod];
    this.setState({ timePeriod, days }, () => {
      this.applyVolumeWithTimePeriod();
    });
  }

  getColumns = () => {
    const { timePeriod } = this.state;
    const { currentVolume, volumeFromSource } = this.props;

    const columnSource = timePeriod === WEEK ? WEEK_COLUMN_KEY : DAY_COLUMN_KEY;
    const columnKeys = flatten([...currentVolume.data, ...volumeFromSource].map(x => x.data))
      .reduce((a, c) => ({ ...a, [c[columnSource]]: true }), {});

    const weekColumn = {
      title: 'Week',
      key: WEEK,
      dataIndex: '',
      className: css`padding-right: 2rem !important;`,
      width: 120,
      align: 'left',
      render: (cell, { source }) => (!source)
        ? null // Collapsible group is considered a row as well. No weeks should be rendered.
        : (
          <div>
            {
              Object.keys(WEEK_DAYS).reduce((arr, curr) => (
                ((typeof cell[curr] === 'number') ? cell[curr] : 0) + arr
              ), 0)
            }
          </div>
        ),
    };

    const volumeManagerColumns = [
      {
        title: '',
        dataIndex: 'area',
        key: 'area',
        render: (_, { area, source }) => source
          ? `${area} - ${source}`
          : area,
      },
      ...Object.keys(columnKeys).map(day => ({
        className: css`padding-right: 2rem !important;`,
        title: day,
        dataIndex: day,
        key: day,
        width: 120,
        align: 'left',
        render: (cell, { area, source }) => {
          if (cell === undefined) return null;
          if (area === 'total' && source === 'modification') {
            return (
              <div style={{ color: 'red' }}>
                {cell.toLocaleString('en')}
              </div>
            );
          }
          if (source === 'modification') {
            return (
              <div style={{ color: 'red' }}>
                {VOLUME_MODIFIERS[cell.modifier].required
                  ? (VOLUME_MODIFIERS[cell.modifier].f(cell.volume, cell.amount)).toLocaleString('en')
                  : ''
                }
              </div>
            );
          }
          return this.volumeComponent({
            day,
            area: { area, source },
            volume: cell,
            onEdit: this.modifyVolume,
          });
        },
      })),
    ];

    if (timePeriod === WEEK) {
      volumeManagerColumns.push(weekColumn);
    }

    return volumeManagerColumns;
  };

  render() {
    const {
      source, weeks, days, volumeType, expandedRows, dateType, timePeriod,
    } = this.state;

    const {
      availableVolumeDataSources, currentVolume, volumeFromSource,
      onUpdateVolumes, updateVolumeStatus, volumeIsLinked, isOptimising,
      isCheckingOptimising,
    } = this.props;

    const tableData = this.combineVolumes(currentVolume.data, volumeFromSource, source);

    const noEdits = isEmpty(this.getEdits().modifiers);

    const columns = this.getColumns();

    return (
      <Wrapper>
        <Loading
          {...!isEmpty(updateVolumeStatus) && { useupdatingtext: true }}
          spinning={
            !isEmpty([...currentVolume.loadingQueue, ...updateVolumeStatus])
          }
        />
        <PageWithTopbar
          py={1}
          rightSpan={1}
          rightComponent={(
            <DisabledTooltip
              placement="left"
              predicates={{
                volumeIsLinked,
                noEdits,
                isOptimising,
                isCheckingOptimising,
              }}
            >
              <Button
                type="primary"
                disabled={noEdits || volumeIsLinked || isOptimising || isCheckingOptimising}
                onClick={() => onUpdateVolumes(
                  this.getEdits(),
                  timePeriod === WEEK ? WEEK : DAY,
                  () => this.onApply(this.state),
                )}
              >
                Save
              </Button>
            </DisabledTooltip>
          )}

          leftSpan={23}
          leftComponent={(
            <>
              <ToolbarSection>
                <strong>View</strong>

                <DayViewSelect
                  value={timePeriod}
                  placeholder="Select a day"
                  onChange={newTimePeriod => this.onSetTimePeriod(newTimePeriod)}
                >
                  <Select.OptGroup label="Full Week">
                    <Option key="week" value="week">Week</Option>
                  </Select.OptGroup>
                  <Select.OptGroup label="Single Day">
                    {FRIENDLY_WEEK_DAY_ARRAY
                      .map(s => (
                        <Option key={s.value} value={s.value}>
                          {s.label}
                        </Option>
                      ))
                    }
                  </Select.OptGroup>
                </DayViewSelect>

              </ToolbarSection>
              <Divider type="vertical" />
              <ToolbarSection>
                <strong>Filters</strong>
                <div>
                  <Radio.Group
                    value={volumeType}
                    buttonStyle="solid"
                    onChange={({ target: { value } }) => this.onApply({ volumeType: value })}
                  >
                    <Radio.Button value="inbound">Inbound</Radio.Button>
                    <Radio.Button value="volume">Outbound</Radio.Button>
                  </Radio.Group>
                  {volumeType === 'volume' && (
                    <Radio.Group
                      value={dateType}
                      buttonStyle="solid"
                      onChange={({ target: { value } }) => this.onApply({ dateType: value })}
                    >
                      <Radio.Button value="order_datetime">Order Date</Radio.Button>
                      <Radio.Button value="datetime">Dispatch Date</Radio.Button>
                    </Radio.Group>
                  )}

                  <VolumePicker
                    state={{ source, weeks, days }}
                    sources={availableVolumeDataSources}
                    datesOverlap={this.datesOverlap}
                    onChange={toApply => this.onApply(toApply)}
                  />
                </div>
              </ToolbarSection>
            </>
          )
          }
        >
          <TableWrapper>
            <ExpandButton
              size="small"
              onClick={() => this.toggleExpandAll(tableData)}
            >
              {(expandedRows.length === tableData.length)
                ? 'Collapse All'
                : 'Expand All'
              }
            </ExpandButton>
            <AreaTable
              expandIcon={({ expanded }) => <Icon type={expanded ? 'down' : 'right'} />}
              expandRowByClick
              rowKey="area"
              expandedRowKeys={expandedRows}
              onExpand={(_, { area }) => this.onExpand(area)}
              columns={columns}
              dataSource={tableData}
              pagination={false}
              expandedRowRender={this.dataTableRender}
              onRow={({ area }) => area === 'total' && { style: { fontWeight: 700 } }}
            />
          </TableWrapper>
        </PageWithTopbar>
      </Wrapper>
    );
  }
}

export default VolumeManager;
